/**
 * Fetch published CMS content for a site section.
 *
 * Returns `defaultValue` on SSR (synchronous), during loading, and on error.
 * Replaces with CMS data after client hydration if the backend has published content.
 *
 * The public endpoint GET /api/v1/content/{sectionKey} returns either the
 * published JSON or the built-in seed — both identical to the hardcoded defaults
 * until an admin publishes a change. So there is no visible flash on first load.
 */
import { useEffect, useState } from 'react';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api/v1';

export function useContent<T>(sectionKey: string, defaultValue: T): T {
  const [data, setData] = useState<T>(defaultValue);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/content/${sectionKey}`)
      .then(r => (r.ok ? r.json() : null))
      .then((json: T | null) => {
        if (!cancelled && json) setData(json);
      })
      .catch(() => {
        // Network error or server unavailable — keep showing default.
      });
    return () => { cancelled = true; };
  }, [sectionKey]);

  return data;
}
